import type { ShopperProducts } from '@/scapi';

type Product = ShopperProducts.schemas['Product'];

/**
 * Locale-aware flag SFRA computes from Global-e attributes, hazmat attributes and category rules.
 * Read defensively — only an explicit `true` claims a restriction.
 */
export const RESTRICTED_ATTR = 'c_isHazmatOrRestricted';

export type ProductAvailabilityKind = 'available' | 'restricted' | 'out-of-stock';

export type ProductAvailability = { kind: ProductAvailabilityKind };

type ProductAvailabilityFields = {
    [RESTRICTED_ATTR]?: boolean;
    /** Backend stock roll-up. Not on the generated SCAPI schema, and not returned today. */
    inStock?: boolean;
    variants?: Array<{ orderable?: boolean }>;
    inventory?: { orderable?: boolean };
};

const asAvailabilityFields = (product: Product): ProductAvailabilityFields =>
    product as unknown as ProductAvailabilityFields;

export const isProductRestricted = (product: Product): boolean =>
    asAvailabilityFields(product)[RESTRICTED_ATTR] === true;

/**
 * `inStock` is the backend roll-up and wins when present, but SCAPI Shopper Products does not
 * currently return it — the variant scan is the operative path. Every variant must be explicitly
 * unorderable; unknown orderability keeps the product purchasable, so missing data never
 * fabricates an out-of-stock state.
 */
export const isProductOutOfStock = (product: Product): boolean => {
    const { inStock, variants, inventory } = asAvailabilityFields(product);

    if (typeof inStock === 'boolean') {
        return !inStock;
    }

    if (variants?.length) {
        return variants.every((variant) => variant.orderable === false);
    }

    return inventory?.orderable === false;
};

/** Restricted outranks out-of-stock: a product that is both must not show out-of-stock messaging. */
export const deriveProductAvailability = (product: Product): ProductAvailability => {
    if (isProductRestricted(product)) {
        return { kind: 'restricted' };
    }

    if (isProductOutOfStock(product)) {
        return { kind: 'out-of-stock' };
    }

    return { kind: 'available' };
};
